/**
 * @file Отображение состояния скачивания и действий пользователя.
 *
 * Успех означает передачу данных браузерному загрузчику.
 * Факт сохранения на физический диск страница не знает.
 */
import { useLocale } from '../../i18n/useLocale'
import TransferProgress from '../transfers/TransferProgress'
import '../uploads/Upload.css'
import { downloadMessages } from './messages'
import { downloadPercent, isDownloadBusy } from './model'
import { useDownload } from './useDownload'

export default function DownloadProgress() {
    const { state, cancel, retry, dismiss } = useDownload()
    const { locale } = useLocale()
    if (!state) return null

    const text = downloadMessages[locale]

    // Доступные действия и процент вычисляются из состояния Provider.
    const busy = isDownloadBusy(state)
    const percent = downloadPercent(state)

    return (
        <section className='task-item upload-task' aria-label={state.name}>
            {/* Название и фаза описывают задачу; алгоритм запускается из Provider. */}
            <h3 className='task-title'>↓ {state.name}</h3>

            <p className='upload-phase' role='status'>
                {state.phase === 'succeeded'
                    ? text.browser
                    : text.phases[state.phase]}
            </p>

            {/* Общий счётчик получает подтверждённые байты; анимация не меняет результат передачи. */}
            <TransferProgress
                id={state.id}
                name={state.name}
                bytes={state.written}
                total={state.size}
                percent={percent}
                active={state.phase === 'downloading'}
            />

            {/* Ошибка передачи и предупреждение очистки имеют разный смысл и показаны отдельно. */}
            {state.error && (
                <p className='upload-error' role='alert'>
                    {text.errors[state.error]}
                </p>
            )}
            {['failed', 'cancelled'].includes(state.phase) &&
                state.written > 0 && (
                    <p className='upload-note'>{text.partial}</p>
                )}
            {state.cleanupWarning && (
                <p className='upload-note'>{text.cleanup}</p>
            )}

            {/* Действия зависят от фазы: во время завершения операции отмена уже недоступна. */}
            <div className='upload-actions'>
                {['preparing', 'checking', 'downloading'].includes(
                    state.phase,
                ) && (
                    <button type='button' onClick={cancel}>
                        {text.cancel}
                    </button>
                )}
                {!busy && state.phase !== 'succeeded' && (
                    <button type='button' onClick={retry}>
                        {text.retry}
                    </button>
                )}
                {!busy && (
                    <button type='button' onClick={dismiss}>
                        {text.dismiss}
                    </button>
                )}
            </div>

            {/* Технические подробности скрыты по умолчанию, чтобы панель оставалась компактной. */}
            <details className='upload-segments'>
                <summary>{text.details}</summary>

                <p className='upload-destination'>
                    {text.storage}: {state.boxId}
                </p>

                <p className='upload-destination'>
                    {text.source}: {state.sourcePath}
                </p>
            </details>
        </section>
    )
}
